import { useUniqueIds } from '@/composables/useUniqueIds';
import { useChartStyleButtons } from '@/composables/useChartStyleButtons';
import { buildUrlString } from '@/utils/buildUrlString';
import { extractStyleCacheId } from '@/utils/extractStyleCacheId';
import { getStyleCacheSettingsOverrides } from '@/utils/getStyleCacheSettingsOverrides';
import { ChartStyle } from '@/interfaces/chart';
import { SampleChartStyleGroup, SimpleFrontendSampleChartStyle } from '@/interfaces/chartStyle';
import { RawSampleChartStyleGroup } from '@/composables/fetchSampleChartStyleGroups';
import { stripHtmlFromString } from '@/plugins/vue-sanitize-html';

export function parseSampleChartStyleGroups(rawSampleChartStyleGroups: RawSampleChartStyleGroup[]): SampleChartStyleGroup[] {
  const { getNegativeUniqueId } = useUniqueIds();
  const { DEFAULT_CHART_STYLE_BUTTON_BACKGROUND_COLOR_OPTION } = useChartStyleButtons();

  const formattedSampleChartStyleGroups = [];

  for (const { rawSampleChartStyleGroupId, rawSampleChartStyles } of rawSampleChartStyleGroups) {
    const formattedSampleChartStyleGroupName = rawSampleChartStyleGroupId
      .split(/(?=[A-Z])/)
      .map(w => w.trim())
      .filter(w => !!w)
      .map(w => `${w[0].toUpperCase()}${w.slice(1)}`)
      .join(' ')
      .replace(/Chart Style/g, 'ChartStyle');

    if (!formattedSampleChartStyleGroupName) {
      window.sc3WorkbenchLogger.warn(`Failed to parse sample ChartStyle group with id: ${rawSampleChartStyleGroupId} - unable to generate a valid sample ChartStyle group name from id.`);
      continue;
    }

    const formattedSampleChartStyles: SimpleFrontendSampleChartStyle[] = [];

    for (const rawSampleChartStyle of rawSampleChartStyles) {
      const styleCacheId = extractStyleCacheId(rawSampleChartStyle.pcode);

      if (!styleCacheId) {
        continue;
      }

      let styleCacheSettingsOverrides: Partial<ChartStyle> = {};

      if (typeof rawSampleChartStyle.sharpChartArguments === 'string') {
        styleCacheSettingsOverrides = getStyleCacheSettingsOverrides(rawSampleChartStyle.sharpChartArguments);
      }

      const name = rawSampleChartStyle.title.trim();
      const htmlSanitizedName = stripHtmlFromString(name);
      const searchTags = [name];
      const thumbnailUrlParameters = { i: styleCacheId };
      const thumbnailUrl = buildUrlString('/c-sc/sc', { areParametersAlreadyUrlEncoded: true, parameters: thumbnailUrlParameters });

      formattedSampleChartStyles.push({
        name,
        htmlSanitizedName,
        recid: getNegativeUniqueId(),
        searchTags,
        styleCacheId,
        styleCacheSettingsOverrides,
        temporaryName: name,
        thumbnailUrl,
        chartStyleButtonIndex: -1,
        chartStyleButtonBackgroundColorHexCode: DEFAULT_CHART_STYLE_BUTTON_BACKGROUND_COLOR_OPTION.hexCode,
        chartStyleButtonTextColorHexCode: DEFAULT_CHART_STYLE_BUTTON_BACKGROUND_COLOR_OPTION.isDarkColor ? '#ffffff' : '#000000',
        chartStyleButtonLabel: '',
        description: rawSampleChartStyle.description?.trim() ?? '',
        isForPayingMembersOnly: rawSampleChartStyle.membersOnly ?? false,
        rootSymbol: rawSampleChartStyle.symbol ?? 'AAPL',
        shouldLoadRootSymbol: rawSampleChartStyle.shouldLoadSymbol ?? false,
      });
    }

    if (!formattedSampleChartStyles.length) {
      window.sc3WorkbenchLogger.warn(`Failed to parse sample ChartStyle group with id: ${rawSampleChartStyleGroupId} and name: ${formattedSampleChartStyleGroupName} - sample ChartStyle group does not contain any valid sample ChartStyles.`);
      continue;
    }

    formattedSampleChartStyleGroups.push({
      name: formattedSampleChartStyleGroupName,
      recid: getNegativeUniqueId(),
      areSavedCharts: false,
      charts: formattedSampleChartStyles
    });
  }

  if (!formattedSampleChartStyleGroups.length) {
    window.sc3WorkbenchLogger.warn('Failed to parse sample ChartStyle groups - all sample ChartStyle groups are empty or invalid.');
  }

  return formattedSampleChartStyleGroups;
}
